import {
  hashKey,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { useEffect, useRef } from "react";
import type { CurrentUserWorkbookAccessResponse } from "@/business/business";
import { currentUserWorkbookAccessKey } from "@/business/query";
import { useApi } from "@/core/api";
import { pollStatus, type PollingRuntimeOptions } from "@/core/api/polling";
import { useAuth } from "@/core/auth";
import { isPayrollAccessError } from "@/payroll/teams/access-error";
import { currentPayrollResult } from "@/payroll/teams/query-result";
import { usePayrollRosterAccess } from "./permissions";
import type {
  CompletedPayrollRosterTechnicianSync,
  PayrollRoster,
  PayrollRosterTechnicianSync,
  SavePayrollRosterDraftPayload,
  SyncPayrollRosterTechniciansResponse,
} from "./types";

export const payrollRosterPath = (businessId: string) =>
  `/businesses/${businessId}/payroll_roster`;

export const payrollRosterDraftPath = (businessId: string) =>
  `${payrollRosterPath(businessId)}/draft`;

export const payrollRosterPublishPath = (businessId: string) =>
  `${payrollRosterPath(businessId)}/publish`;

export const payrollRosterSyncTechniciansPath = (businessId: string) =>
  `${payrollRosterPath(businessId)}/sync_technicians`;

export const payrollRosterTechnicianSyncStatusPath = (
  businessId: string,
  syncId: string,
) => `${payrollRosterSyncTechniciansPath(businessId)}/${syncId}`;

type RosterQueryScope = {
  userId: string | undefined;
  visibility:
    | CurrentUserWorkbookAccessResponse["roster_visibility"]
    | undefined;
};
export const payrollRosterKey = (
  businessId: string | null,
  viewer?: RosterQueryScope,
) =>
  viewer
    ? (["payroll-roster", businessId, viewer] as const)
    : (["payroll-roster", businessId] as const);

const useRosterViewer = (businessId: string | null) => {
  const { user } = useAuth();
  const access = usePayrollRosterAccess(businessId);
  return {
    access,
    viewer: { userId: user?.id, visibility: access.data?.roster_visibility },
  };
};

export const payrollRosterVersionKey = (
  businessId: string | null,
  version: number | null,
  viewer?: RosterQueryScope,
) => [...payrollRosterKey(businessId, viewer), "version", version] as const;

// A specific roster version (fields + members), including archived versions —
// the heavy snapshot the read-only Roster tab loads lazily. The server returns
// 204 when the version does not exist.
export const payrollRosterVersionQueryOptions = (
  api: AxiosInstance,
  businessId: string | null,
  version: number | null,
  enabled = true,
) => ({
  queryKey: payrollRosterVersionKey(businessId, version),
  queryFn: async ({ signal }: { signal?: AbortSignal } = {}) => {
    if (!businessId || version == null) throw new Error("Version is required");

    const response = await api.get<PayrollRoster | null>(
      payrollRosterPath(businessId),
      { params: { version }, signal },
    );

    return response.status === 204 ? null : response.data;
  },
  enabled: enabled && !!businessId && version != null,
  retry: false,
  // Historical content is fixed, but current viewer access can change.
  staleTime: 0,
  refetchOnMount: "always" as const,
  refetchInterval: 30_000,
});

export const usePayrollRosterVersion = (
  businessId: string | null,
  version: number | null,
  enabled = true,
) => {
  const api = useApi();

  return useAuthorizedRosterQuery(
    businessId,
    payrollRosterVersionQueryOptions(api, businessId, version, enabled),
    version,
  );
};

export const payrollRosterQueryOptions = (
  api: AxiosInstance,
  businessId: string | null,
) => ({
  queryKey: payrollRosterKey(businessId),
  queryFn: async ({ signal }: { signal?: AbortSignal } = {}) => {
    if (!businessId) throw new Error("Business is required");

    const response = await api.get<PayrollRoster | null>(
      payrollRosterPath(businessId),
      { signal },
    );

    return response.status === 204 ? null : response.data;
  },
  enabled: !!businessId,
  staleTime: 0,
  refetchOnMount: "always" as const,
  retry: false,
});

// Every roster consumer, including historical versions and team selectors, waits
// for current API access. Viewer-scoped keys prevent reuse across users/grants.
const useAuthorizedRosterQuery = (
  businessId: string | null,
  options: Omit<ReturnType<typeof payrollRosterQueryOptions>, "queryKey">,
  version?: number | null,
) => {
  const client = useQueryClient();
  const { access, viewer } = useRosterViewer(businessId);
  const allowed = access.canReadRoster;
  const queryKey =
    version === undefined
      ? payrollRosterKey(businessId, viewer)
      : payrollRosterVersionKey(businessId, version, viewer);
  const queryHash = hashKey(queryKey);
  useEffect(
    () => () => {
      // Query's gcTime takes the largest value ever used for a key. Explicitly
      // remove unused entries too, including caches seeded before these guards.
      queueMicrotask(() =>
        client.removeQueries({
          predicate: (cached) =>
            cached.queryHash === queryHash && cached.getObserversCount() === 0,
        }),
      );
    },
    [client, queryHash],
  );
  const query = useQuery({
    ...options,
    queryKey,
    enabled: options.enabled && allowed,
    gcTime: 0,
    refetchInterval: 30_000,
    queryFn: async (context) => {
      try {
        return await options.queryFn(context);
      } catch (error) {
        if (isPayrollAccessError(error) && !context.signal.aborted) {
          // A permitted team read can have no members in this version (404).
          // Other versions can still contain authorized members.
          if (error.status === 404) {
            client.setQueryData(queryKey, null);
            throw error;
          }
          // Cancel sibling reads before clearing data so an older request cannot
          // put a formerly authorized roster back into the cache.
          await client.cancelQueries({
            queryKey: payrollRosterKey(businessId),
            predicate: (cached) => cached.queryHash !== queryHash,
          });
          client.setQueriesData(
            { queryKey: payrollRosterKey(businessId) },
            null,
          );
          if (error.status === 403) {
            // Re-check effective visibility; a forbidden version alone does not
            // establish that every roster version is forbidden.
            void client.invalidateQueries({
              queryKey: currentUserWorkbookAccessKey(businessId, viewer.userId),
            });
          }
        }
        throw error;
      }
    },
  });
  useEffect(() => {
    if (
      allowed ||
      !businessId ||
      (!access.isError && !access.isFetchedAfterMount)
    )
      return;
    let active = true;
    void client
      .cancelQueries({ queryKey: payrollRosterKey(businessId) })
      .then(() => {
        if (active)
          client.setQueriesData(
            { queryKey: payrollRosterKey(businessId) },
            null,
          );
      });
    return () => {
      active = false;
    };
  }, [allowed, access.isError, access.isFetchedAfterMount, businessId, client]);
  const result = currentPayrollResult(query);
  return {
    ...result,
    data: allowed ? result.data : undefined,
    isLoading: result.isLoading || (options.enabled && access.isAccessLoading),
  };
};

export const usePayrollRoster = (businessId: string | null) => {
  const api = useApi();
  return useAuthorizedRosterQuery(
    businessId,
    payrollRosterQueryOptions(api, businessId),
  );
};

export const replacePayrollRosterSuggestions = (
  current: PayrollRoster | null | undefined,
  synced: CompletedPayrollRosterTechnicianSync,
) =>
  current
    ? {
        ...current,
        missing_user_suggestions: synced.missing_user_suggestions,
        departed_member_ids: synced.departed_member_ids,
      }
    : current;

export const buildWorkingRosterCacheEntry = (
  saved: PayrollRoster,
  current?: PayrollRoster | null,
): PayrollRoster => ({
  ...current,
  ...saved,
  is_draft: true,
});

export const buildDiscardedRosterCacheEntry = (
  current?: PayrollRoster | null,
): PayrollRoster | null | undefined => {
  if (!current) return current;
  if (!current.baseline) return null;

  return {
    ...current,
    version_number: current.baseline.version_number,
    status: "published",
    published_at: current.baseline.published_at,
    is_draft: false,
    fields: current.baseline.fields,
    members: current.baseline.members,
    removed_member_external_ids: current.baseline.removed_member_external_ids,
  };
};

export const pollPayrollRosterTechnicianSync = async ({
  initial,
  request,
  signal,
  now,
  wait,
}: PollingRuntimeOptions & {
  initial: PayrollRosterTechnicianSync;
  request: (signal: AbortSignal) => Promise<PayrollRosterTechnicianSync>;
  signal: AbortSignal;
}): Promise<CompletedPayrollRosterTechnicianSync> => {
  const result = await pollStatus({
    initial,
    isPending: (sync) => sync.status === "queued" || sync.status === "running",
    request,
    signal,
    now,
    wait,
  });

  if (result.polling_timed_out) {
    throw new Error("Technician sync timed out. Try again.");
  }
  if (result.status === "failed") {
    throw new Error(result.error ?? "Technician sync failed");
  }
  if (result.status !== "completed") {
    throw new Error("Technician sync ended with an unknown status");
  }

  return result;
};

export const syncPayrollRosterTechnicians = async (
  api: AxiosInstance,
  businessId: string,
  options: PollingRuntimeOptions & {
    signal?: AbortSignal;
  } = {},
) => {
  const signal = options.signal ?? new AbortController().signal;
  const response = await api.post<SyncPayrollRosterTechniciansResponse>(
    payrollRosterSyncTechniciansPath(businessId),
    undefined,
    { signal },
  );
  const initial = response.data.technician_sync;

  return pollPayrollRosterTechnicianSync({
    initial,
    signal,
    now: options.now,
    wait: options.wait,
    request: (requestSignal) =>
      api
        .get<SyncPayrollRosterTechniciansResponse>(
          payrollRosterTechnicianSyncStatusPath(businessId, initial.id),
          { signal: requestSignal },
        )
        .then((pollResponse) => pollResponse.data.technician_sync),
  });
};

export const useSyncPayrollRosterTechnicians = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { viewer } = useRosterViewer(businessId);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      controllerRef.current?.abort();
    },
    [],
  );

  return useMutation({
    mutationFn: async () => {
      if (!businessId) throw new Error("Business is required");

      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;

      try {
        return await syncPayrollRosterTechnicians(api, businessId, {
          signal: controller.signal,
        });
      } catch (error) {
        if (controller.signal.aborted) {
          const canceled = new Error("Technician sync canceled");
          canceled.name = "AbortError";
          throw canceled;
        }
        throw error;
      } finally {
        if (controllerRef.current === controller) controllerRef.current = null;
      }
    },
    onSuccess: (synced) => {
      queryClient.setQueryData<PayrollRoster | null>(
        payrollRosterKey(businessId, viewer),
        (current) => replacePayrollRosterSuggestions(current, synced),
      );
    },
  });
};

// PUT /draft — upserts the persisted draft (created from published on first
// save). Send the full working roster. Merge the authoritative response into
// the query cache while preserving GET-only metadata that the response omits.
export const useSavePayrollRosterDraft = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { viewer } = useRosterViewer(businessId);

  return useMutation({
    mutationFn: async (payload: SavePayrollRosterDraftPayload) => {
      if (!businessId) throw new Error("Business is required");

      return api
        .put<PayrollRoster>(payrollRosterDraftPath(businessId), payload)
        .then((response) => response.data);
    },
    onSuccess: (savedRoster) => {
      queryClient.setQueryData<PayrollRoster | null>(
        payrollRosterKey(businessId, viewer),
        (current) => buildWorkingRosterCacheEntry(savedRoster, current),
      );
    },
  });
};

// After a publish, the returned roster is authoritative: it becomes BOTH the new
// working copy and the new baseline, and no draft remains. Build the complete
// GET-shaped cache entry (with `is_draft` + `baseline`) from that response. Keep
// GET-only suggestion state from the current cache because the publish response
// does not include it. The editor reconciles without depending on the follow-up
// GET, whose refetch errors `invalidateQueries` swallows by default.
export const buildPublishedRosterCacheEntry = (
  published: PayrollRoster,
  current?: PayrollRoster | null,
): PayrollRoster => ({
  ...current,
  ...published,
  is_draft: false,
  baseline: {
    version_number: published.version_number,
    published_at: published.published_at,
    fields: published.fields,
    members: published.members,
    removed_member_external_ids: published.removed_member_external_ids,
  },
});

// POST /publish — promotes the draft to published (archives the prior
// version). Takes no body; 422s if there is no draft or the shape violates the
// contract. Returns the newly published roster for the query cache.
export const usePublishPayrollRoster = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { viewer } = useRosterViewer(businessId);

  return useMutation({
    mutationFn: async () => {
      if (!businessId) throw new Error("Business is required");

      return api
        .post<PayrollRoster>(payrollRosterPublishPath(businessId))
        .then((response) => response.data);
    },
    onSuccess: (publishedRoster) => {
      // Reconcile from the authoritative publish response so a failed canonical
      // GET can't leave a stale baseline (see buildPublishedRosterCacheEntry).
      queryClient.setQueryData<PayrollRoster | null>(
        payrollRosterKey(businessId, viewer),
        (current) => buildPublishedRosterCacheEntry(publishedRoster, current),
      );
      // Best-effort background refresh for any server-recomputed fields; harmless
      // if it fails, because the cache above is already correct.
      void queryClient.invalidateQueries({
        queryKey: payrollRosterKey(businessId),
      });
    },
  });
};

// DELETE /draft — discards the draft, reverting to published. The API returns
// 204 No Content: the 204 alone IS the success signal (the draft is gone). We do
// NOT chain a GET into the mutation — a failed refresh must not report the
// already-completed discard as failed. The invalidation refetches the canonical
// roster separately.
export const useDiscardPayrollRosterDraft = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { viewer } = useRosterViewer(businessId);

  return useMutation({
    mutationFn: async () => {
      if (!businessId) throw new Error("Business is required");

      await api.delete(payrollRosterDraftPath(businessId));
    },
    onSuccess: () => {
      // The DELETE has already made the published baseline authoritative. Move
      // the cache to that server-provided baseline before the refresh starts so
      // a failed GET cannot leave the deleted draft visible.
      queryClient.setQueryData<PayrollRoster | null>(
        payrollRosterKey(businessId, viewer),
        buildDiscardedRosterCacheEntry,
      );

      // Keep the mutation pending until the server refresh settles. The
      // baseline above remains a safe fallback if this best-effort GET fails.
      return queryClient.invalidateQueries({
        queryKey: payrollRosterKey(businessId),
      });
    },
  });
};
